import React from 'react';
import { Flex, Skeleton, Card, Button } from 'antd';
import { 
  HomeOutlined, ShoppingCartOutlined, ShoppingOutlined, ShopOutlined,
  TeamOutlined, ApiOutlined, ScanOutlined, ShareAltOutlined,
  BarChartOutlined, SolutionOutlined, GiftOutlined, PartitionOutlined,
  PlusCircleOutlined, EditOutlined, MobileOutlined
} from '@ant-design/icons';
import AppIconGroup from '../../components/app-icon-group';
import { sendUserLoginToApp, SafeStorage, detectWebView } from '../../utils/jsbridge';
import { openWxminiShop } from '../shop/tools';

const { isWebView } = detectWebView();

const Index = () => {
  const [loading, setLoading] = React.useState(true);
  const [hasAbility, setHasAbility] = React.useState(false);

  const getMyShops = async () => {
    let response = await fetch(`/api/shop/my-permission-list`);
    let result = await response.json();
    if(result && result.success) {
        return result.data;
    }
    return [];
  }

  const toMiniShop = async () => {
    const { accId, shopId } = (await getMyShops()).filter(item => item.isHead)?.[0] || {};
    if(accId && shopId) await openWxminiShop(accId, shopId);
  }

  // iOS 风格的应用图标配置 - 精简配色
  const appIcons = [
    {
      id: 1,
      title: '店铺',
      href: '/shop',
      icon: <HomeOutlined />,
      color: 'from-blue-500 to-blue-600',
      permission: { action: 'read', subject: 'shop' }
    },
    {
      id: 2,
      title: '订单',
      href: '/order',
      icon: <ShoppingCartOutlined />,
      color: 'from-green-500 to-green-600',
      permission: { action: 'read', subject: 'order' }
    },
    {
      id: 3,
      title: '商品',
      href: '/goods',
      icon: <ShoppingOutlined />,
      color: 'from-orange-500 to-orange-600',
      permission: { action: 'read', subject: 'goods' }
    },
    {
      id: 4,
      title: '装修',
      href: '/mall',
      icon: <ShopOutlined />,
      color: 'from-purple-500 to-purple-600',
      permission: { action: 'read', subject: 'mall' }
    },
    {
      id: 5,
      title: '客户',
      href: '/customer',
      icon: <SolutionOutlined />,
      color: 'from-blue-500 to-blue-600',
      permission: { action: 'read', subject: 'customer' }
    },
    {
      id: 6,
      title: '联盟',
      href: '/salesnet',
      icon: <PartitionOutlined />,
      color: 'from-blue-500 to-blue-600',
      permission: { action: 'read', subject: 'shop' }
    },
    {
      id: 7,
      title: '营销',
      href: '/promotion',
      icon: <GiftOutlined />,
      color: 'from-red-500 to-red-600',
      permission: { action: 'read', subject: 'promotion' }
    },
    {
      id: 8,
      title: '渠道',
      href: '/channel',
      icon: <ApiOutlined />,
      color: 'from-blue-500 to-blue-600',
      permission: { action: 'read', subject: 'channel' },
      condition: window.__baseInfo.versionId !== 1
    },
    {
      id: 9,
      title: '账号',
      href: '/account',
      icon: <TeamOutlined />,
      color: 'from-gray-500 to-gray-600',
      permission: { action: 'read', subject: 'account' }
    },
    {
      id: 10,
      title: '财务',
      href: '/finance',
      icon: <BarChartOutlined />,
      color: 'from-green-500 to-green-600',
      permission: { action: 'read', subject: 'finance' }
    },
    {
      id: 11,
      title: '核销',
      href: '/writeoff',
      icon: <ScanOutlined />,
      color: 'from-green-500 to-green-600',
      permission: { action: 'read', subject: 'order' }
    },
    {
      id: 12,
      title: '邀请',
      href: '/join',
      icon: <ShareAltOutlined />,
      color: 'from-blue-500 to-blue-600',
      permission: { action: 'read', subject: 'order' }
    }
  ];

  const shortcuts = [
    {
      id: 1,
      title: '发布商品',
      description: '快速发布新商品',
      href: '/goods/create',
      icon: <PlusCircleOutlined />,
      color: '#1890ff'
    },
    {
      id: 2,
      title: '小程序装修',
      description: '美化店铺页面',
      href: '/mall',
      icon: <EditOutlined />,
      color: '#52c41a'
    },
    {
      id: 3,
      title: '查看小程序',
      description: '预览小程序店铺',
      onClick: toMiniShop,
      icon: <MobileOutlined />,
      color: '#faad14'
    }
  ];

  React.useEffect(() => {
    const checkUserAbility = async () => {
      try {
        // 异步等待权限刷新（App.js 中 getUserAblity 会调接口并写入 SafeStorage）
        // 等一段时间重试，避免 App.js 接口还没返回时 SafeStorage 为空
        for (let i = 0; i < 10; i++) {
          const userAbility = await SafeStorage.getItem('user-ability', true);
          if (userAbility) {
            setHasAbility(true);
            return;
          }
          // App.js 还没写完，等 500ms 再试
          await new Promise(r => setTimeout(r, 500));
        }
        // 10次重试后仍没有，说明确实无权限或接口异常，降级显示
        console.warn('User ability not found after retries, showing limited UI');
      } catch (e) {
        console.error('Failed to get user ability:', e);
      } finally {
        setLoading(false);
      }
    };

    checkUserAbility();
  }, []);

  React.useEffect(() => {
    sendUserLoginToApp({
        userId: window.__baseInfo.userId,
    });
  })

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone ||
                       document.referrer.includes('android-app://');
  
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                /Macintosh/.test(navigator.userAgent);

  if (loading) {
    return <Skeleton active />;
  }

  if (!hasAbility) {
    return <div className="admin-content">暂无权限</div>;
  }

  return (
    <div>
      {/* Banner 区域 */}
      <div className="pt-4 pb-3">
        <a 
          onClick={toMiniShop}
          className="relative block w-full h-20 rounded-2xl overflow-hidden shadow-lg active:scale-95 transition-transform"
          style={{ 
            background: 'url("https://img.yxdj.tech/yxdj-system/example/banner.png") no-repeat center center',
            backgroundSize: 'cover',
          }}
        >
          {/* 渐变遮罩 */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent"></div>
          
          {/* 标题文案 */}
          <div className="absolute inset-0 flex items-center px-5">
            <h2 className="text-white text-lg md:text-xl font-bold">点击查看我的小程序</h2>
          </div>
        </a>
      </div>

      {/* iOS 风格的应用图标网格 */}
      <AppIconGroup items={appIcons} />

      {/* 快捷操作卡片 */}
      <div className="py-4">
        <Card 
          className="rounded-2xl shadow-sm border-0"
          title={
            <span className="text-base font-semibold text-gray-800">快捷操作</span>
          }
          bodyStyle={{ padding: '12px' }}
        >
          <div className="space-y-2">
            {shortcuts.map(shortcut => (
              <div
                key={shortcut.id}
                onClick={() => shortcut.onClick ? shortcut.onClick() : (shortcut.href && (window.location.href = shortcut.href))}
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 
                  cursor-pointer transition-colors min-h-touch"
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg flex-shrink-0"
                  style={{ backgroundColor: shortcut.color }}
                >
                  {shortcut.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-800 mb-0.5">
                    {shortcut.title}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {shortcut.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      
      {/* APP 下载按钮 */}
      {!isWebView && !isStandalone && !isIOS && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 w-[90%] max-w-[400px] px-4 z-40">
          <Button 
            type="primary" 
            size="large"
            href='https://img.yxdj.tech/yxdj-system/app/latest.apk'
            className="w-full min-h-touch text-base font-semibold rounded-xl active:scale-95 transition-transform 
              bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 border-0 shadow-lg"
          >
            下载优选到家 APP
          </Button>
        </div>
      )}
    </div>
  )
}

export default Index;